import toast from 'react-hot-toast';
import type { AxiosInstance, AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios';
import axios from 'axios';
import type { ApiResponse } from "./apiType";
import { getLocalInfo } from 'src/utils/encrypt';

declare module 'axios' {
  interface AxiosRequestConfig {
    successMessage?: string;
  }
}

export class ApiService {
  private axiosInstance: AxiosInstance;

  private static handleError(error: AxiosError<any>) {
    if (error.response) {
      const messages = {
        404: 'Resource not found',
        500: 'Server error. Please try again later.',
      };
      const errorMessage =
        messages[(error.response?.status as keyof typeof messages) || ''] ||
        messages[error.response.status as keyof typeof messages] || 
        error.response.data?.detail || 
        `Unexpected error: ${error.response.status}`;

      toast.error(errorMessage, { id: 'api-error' });
      console.error('Full error:', error);
      return;
    }

    if (error.request) {
      toast.error('No response from server. Check your network connection.', {
        id: 'api-error',
      });
      console.error('Full error:', error);
      return;
    }

    toast.error('Error setting up the request', { id: 'api-error' });
    console.error('Full error:', error);
  }

  constructor() {
    const baseURL = import.meta.env.VITE_BASE_URL;
    this.axiosInstance = axios.create({
      baseURL: `${baseURL}/api/v1/`,
    });
    this.axiosInstance.interceptors.request.use(
      (requestConfig) => {
        const accessToken = getLocalInfo('accessToken');
        if (accessToken) {
          requestConfig.headers.Authorization = `Bearer ${accessToken}`;
        }
        requestConfig.headers.createDate = new Date().valueOf();
        return requestConfig;
      },
      (error) => {
        ApiService.handleError(error);
        return Promise.reject(error);
      }
    );

    this.axiosInstance.interceptors.response.use(
      (response) => {
        const successMessage = (response.config).successMessage ||
          `${response.config.method?.toUpperCase() || ''} request successful`;
        return response;
      },
      (error) => {
        ApiService.handleError(error);
        return Promise.reject(error);
      }
    );
  }

  private async request<T>(config: AxiosRequestConfig): Promise<ApiResponse<T>> {
    const response: AxiosResponse<{ success: boolean; message: string; data: T; errors: any[] }> = await this.axiosInstance(config);
    return {
      data: response.data.data,
      status: response.status,
      ok: response.data.success,
      message: response.data.message,
      errors: response.data.errors,
    };
  }

  public async get<T>(url: string, options?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    return this.request<T>({
      ...options,
      method: 'GET',
      url,
      params: options?.params,
    });
  }

  public async post<T, D = unknown>(
    url: string,
    data: D,
    options?: AxiosRequestConfig
  ): Promise<ApiResponse<T>> {
    return this.request<T>({
      ...options,
      method: 'POST',
      url,
      data,
    });
  }

  public async put<T, D = unknown>(
    url: string,
    data: D,
    options?: AxiosRequestConfig
  ): Promise<ApiResponse<T>> {
    return this.request<T>({
      ...options,
      method: 'PUT',
      data,
      url,
    });
  }

  public async delete<T>(url: string, options?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    return this.request<T>({
      ...options,
      method: 'DELETE',
      url,
    });
  }
}
